import { differenceInMinutes, addMinutes, startOfDay, subMinutes, endOfDay } from 'date-fns';
import { Resizable, ResizeCallback, ResizeStartCallback } from 're-resizable';
import React from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { TDay } from '../../../../../../types/e-visits';
import { IAddEvent, ICalendarEvent, TEventType } from '../../../calendars';
import { getEventPosition, isElementXPercentInViewport, WCS } from '../../week-utils';
import { HandleDown, HandleUp } from './add-item.styled';

const MIN_SIZE_INTERVAL = WCS.cellHeight / (WCS.minutesPerHour / WCS.minSelectInterval);

const RESIZE_CONFIG_ENABLED = {
  top: true,
  bottom: true,
};
const RESIZE_CONFIG_DISABLED = {
  top: false,
  right: false,
  bottom: false,
  left: false,
};

interface AddItemProps {
  addEvent?: IAddEvent;
  setAddEvent: React.Dispatch<React.SetStateAction<IAddEvent | undefined>>;
  weekDays: TDay[];
  minHour: number;
  timeLineWidth: number;
  renderAddEvent?: (event: ICalendarEvent<any>) => React.ReactElement;
  onEditItemChange?: (original: ICalendarEvent<any>, modified: ICalendarEvent<any>) => void;
  updateMenuPosition: (
    nodeRef: React.RefObject<HTMLDivElement>,
    event: ICalendarEvent,
    eventType: TEventType,
    index?: number,
  ) => void;
}

const AddItem: React.FunctionComponent<AddItemProps> = ({
  addEvent,
  setAddEvent,
  weekDays,
  minHour,
  timeLineWidth,
  renderAddEvent,
  onEditItemChange,
  updateMenuPosition,
}) => {
  const aRef = React.useRef<HTMLDivElement>(null);
  const [modified, setModified] = React.useState<ICalendarEvent<any> | undefined>(addEvent?.copy);

  /**
   * Handle drag of item o Y axis
   */
  const handleDrag = React.useCallback<DraggableEventHandler>(
    (e, data) => {
      setAddEvent((o) => {
        if (!o || o.isResizing || data.deltaY === 0) {
          return o;
        }

        const start = new Date(o.copy!.start);
        const end = new Date(o.copy!.end);
        const duration = Math.abs(differenceInMinutes(start, end));
        const newStartMinutes = Math.round(
          (data.lastY - WCS.dayHeaderHeight - WCS.gridPadding) / WCS.minuteHeight,
        );

        const newStart = addMinutes(startOfDay(start), newStartMinutes);
        const newEnd = addMinutes(newStart, duration);

        const newEvent = {
          ...o!.copy,
          end: newEnd.toISOString(),
          start: newStart.toISOString(),
        };

        onEditItemChange?.(o!.original, newEvent);

        return {
          ...o!,
          copy: newEvent,
        };
      });
    },
    [setAddEvent, onEditItemChange],
  );

  /**
   * Callback on resize ond
   */
  const handleResizeStart = React.useCallback<ResizeStartCallback>(
    () =>
      setAddEvent((o) => {
        setModified(o!.copy);
        return { ...o!, isResizing: true };
      }),
    [setAddEvent],
  );

  /**
   * Move item on resize
   */
  const handleResize = React.useCallback<ResizeCallback>(
    (event, direction, _ref, delta) => {
      event.preventDefault();
      event.stopPropagation();

      setAddEvent((old) => {
        if (!old) {
          return old;
        }

        const addSize = Math.round(delta.height / MIN_SIZE_INTERVAL);

        let startDate = new Date(modified!.start);
        let endDate = new Date(modified!.end);

        if (direction.includes('top')) {
          if (addSize > 0) {
            startDate = subMinutes(startDate, Math.abs(addSize * WCS.minSelectInterval));
          } else {
            startDate = addMinutes(startDate, Math.abs(addSize * WCS.minSelectInterval));
          }
        } else if (direction.includes('bottom')) {
          if (addSize < 0) {
            endDate = subMinutes(endDate, Math.abs(addSize * WCS.minSelectInterval));
          } else {
            endDate = addMinutes(endDate, Math.abs(addSize * WCS.minSelectInterval));
          }
        }

        const minTime = old.copy.minTime ? startDate < new Date(old.copy.minTime) : false;
        const maxTime = old.copy.maxTime ? endDate > new Date(old.copy.maxTime) : false;

        if (
          (old!.copy.end! === endDate.toDateString() &&
            old!.copy.start === startDate.toDateString()) ||
          minTime ||
          maxTime
        ) {
          return old;
        }

        const newEvent = {
          ...old!.copy,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
        };

        onEditItemChange?.(old!.original, newEvent);

        return {
          ...old!,
          copy: newEvent,
        };
      });
    },
    [setAddEvent, modified, onEditItemChange],
  );

  /**
   * On resize end callback
   */
  const handleResizeEnd = React.useCallback<ResizeCallback>(() => {
    setAddEvent((o) => ({ ...o!, isResizing: false }));
  }, [setAddEvent]);

  /**
   * Handle item click
   */
  const handleItemClick = React.useCallback((ae: IAddEvent) => {
    if (!ae) {
      return;
    }

    const position = aRef.current?.getBoundingClientRect()!;

    if (!isElementXPercentInViewport(position, 30)) {
      window.scrollTo({
        left: position.left,
        top: position.top + window.scrollY - 90,
        behavior: 'smooth',
      });
    }

    updateMenuPosition?.(aRef, ae.copy, 'addEvent');
  }, []);

  const date = addEvent ? new Date(addEvent.copy.start) : undefined;
  const { top, height } =
    addEvent && date
      ? getEventPosition({
          from: date,
          to: new Date(addEvent.copy.end),
          minHour: minHour,
        })
      : { top: 0, height: 0 };

  const movementLimits = React.useMemo(() => {
    if (!addEvent || (!addEvent.copy.minTime && !addEvent.copy.maxTime)) {
      return;
    }

    const minTime = addEvent.copy.minTime
      ? new Date(addEvent.copy.minTime)
      : startOfDay(Date.now());
    const maxTime = addEvent.copy.maxTime ? new Date(addEvent.copy.maxTime) : endOfDay(Date.now());

    const bounds = getEventPosition({
      from: minTime,
      to: maxTime,
      minHour: minHour,
    });

    return {
      top: bounds.top + WCS.dayHeaderHeight + WCS.gridPadding,
      bottom: bounds.top + bounds.height - height + WCS.dayHeaderHeight + WCS.gridPadding,
      maxHeight: Math.abs(differenceInMinutes(minTime, maxTime)) * WCS.minuteHeight,
    };
  }, [addEvent, minHour, height]);

  React.useLayoutEffect(() => {
    if (addEvent) {
      handleItemClick(addEvent);
    }
  }, [addEvent?.copy.start, addEvent?.copy.end, addEvent?.original.start, addEvent?.original.end]);

  if (!addEvent) {
    return null;
  }

  const weekDay = date ? date.getDay() : 0;
  const column = date ? weekDays.findIndex((wd) => wd === weekDay) : 0;

  return (
    <Draggable
      axis="y"
      bounds={{
        top: movementLimits?.top,
        bottom: movementLimits?.bottom,
      }}
      position={{
        x: column * timeLineWidth,
        y: top + WCS.dayHeaderHeight + WCS.gridPadding,
      }}
      disabled={!addEvent.enableGestures || addEvent.isResizing}
      onDrag={(e, data) => {
        if (addEvent.isResizing) {
          return false;
        }
        handleDrag(e, data);
      }}
      grid={[MIN_SIZE_INTERVAL, MIN_SIZE_INTERVAL]}
      onStop={handleDrag}
      onStart={handleDrag}
    >
      <Resizable
        size={{
          height,
          width: timeLineWidth * 0.9,
        }}
        grid={[0, MIN_SIZE_INTERVAL]}
        onResizeStart={handleResizeStart}
        onResize={handleResize}
        onResizeStop={handleResizeEnd}
        enable={addEvent.enableGestures ? RESIZE_CONFIG_ENABLED : RESIZE_CONFIG_DISABLED}
        minHeight={MIN_SIZE_INTERVAL}
        maxHeight={movementLimits?.maxHeight}
        style={styles.resizable}
        handleStyles={{
          top: styles.top,
          bottom: styles.bottom,
        }}
        handleComponent={{
          top: <HandleUp />,
          bottom: <HandleDown />,
        }}
      >
        <div style={styles.content} ref={aRef}>
          {renderAddEvent?.(addEvent.copy)}
        </div>
      </Resizable>
    </Draggable>
  );
};

const styles = {
  top: {
    position: 'absolute',
    height: 14,
    width: '100%',
    top: -14,
    display: 'flex',
    justifyContent: 'center',
  },
  bottom: {
    position: 'absolute',
    height: 14,
    width: '100%',
    bottom: -14,
    display: 'flex',
    justifyContent: 'center',
  },
  resizable: {
    width: '100%',
    height: '100%',
    zIndex: 10000,
    position: 'absolute',
    borderRadius: 5,
    cursor: 'grab',
    filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.08))',
  },
  content: { width: '100%', height: '100%', position: 'relative', zIndex: 1 },
} as Readonly<Record<string, React.CSSProperties>>;

export default React.memo(AddItem);
